import type { ContactPodSaveHandler } from '@/features/contact/ui/contact-pod-button';
import { useModalStore } from '@/shared/lib/store/model/modal.store';
import { Dropdown } from '@/shared/ui';
import { useEffect, useState } from 'react';
import './pod-v2-contact-popup.scss';

const ERROR_TYPES = [
    { value: 'ITEM', label: '문항 오류' },
    { value: 'ANSWER', label: '답안 및 해설 오류' },
    { value: 'PRINT', label: '시험지 출력 기능 오류' },
    { value: 'ETC', label: '기타' },
] as const;

type PodV2ContactPopupProps = {
    articleOptions: { articleId: string; questionNumber: string }[];
    currentTitle: string;
    onSave: ContactPodSaveHandler;
};

export const PodV2ContactPopup = ({
    articleOptions,
    currentTitle,
    onSave,
}: PodV2ContactPopupProps) => {
    const { closeModal } = useModalStore();
    const [errorType, setErrorType] = useState('ITEM');
    const [questionIndex, setQuestionIndex] = useState(0);
    const [title, setTitle] = useState('');
    const [contents, setContents] = useState('');
    const isQuestionInquiry = errorType === 'ITEM' || errorType === 'ANSWER';
    const selectedArticleId = articleOptions[questionIndex]?.articleId ?? '';
    const contentsPlaceholder =
        errorType === 'ETC'
            ? '문의 내용을 구체적으로 입력해 주세요.'
            : errorType === 'PRINT'
              ? '오류가 발생한 문항번호(예: 3번)를 반드시 기입하고, 출력 상황을 구체적으로 입력해 주세요.'
              : '선택한 문항에서 확인한 오류 내용을 구체적으로 입력해 주세요.';

    useEffect(() => {
        if (questionIndex >= articleOptions.length) setQuestionIndex(0);
    }, [articleOptions, questionIndex]);

    const submit = () => {
        const articleIdsForRequest = isQuestionInquiry
            ? selectedArticleId
            : [...new Set(articleOptions.map((item) => item.articleId))].join(',');
        onSave(title, contents, errorType, articleIdsForRequest);
    };

    return (
        <div className="pod-v2-contact-popup" role="presentation">
            <section
                aria-labelledby="pod-v2-contact-title"
                aria-modal="true"
                className="pod-v2-contact-popup__dialog"
                role="dialog"
            >
                <header className="pod-v2-contact-popup__header">
                    <div>
                        <h2 id="pod-v2-contact-title">문항오류 문의하기</h2>
                        <p>비바샘 회원정보의 이메일로 답변이 발송됩니다.</p>
                    </div>
                    <button
                        aria-label="팝업 닫기"
                        className="pod-v2-contact-popup__close"
                        onClick={() => closeModal('contact-popup')}
                        type="button"
                    >
                        ×
                    </button>
                </header>

                <form
                    className="pod-v2-contact-popup__form"
                    onSubmit={(event) => {
                        event.preventDefault();
                        submit();
                    }}
                >
                    <fieldset className="pod-v2-contact-popup__types">
                        <legend className="sr-only">문의 유형</legend>
                        {ERROR_TYPES.map((item) => (
                            <label key={item.value}>
                                <input
                                    checked={errorType === item.value}
                                    name="pod-v2-error-type"
                                    onChange={() => setErrorType(item.value)}
                                    type="radio"
                                    value={item.value}
                                />
                                <span>{item.label}</span>
                            </label>
                        ))}
                    </fieldset>

                    <section className="pod-v2-contact-popup__selection">
                        <strong>문의 문항 선택</strong>
                        <div className="pod-v2-contact-popup__paper-field">
                            <span>자료명</span>
                            <Dropdown
                                btnName={currentTitle}
                                className="pod-v2-contact-popup__dropdown"
                                option={[{ name: currentTitle, val: currentTitle }]}
                                selected={currentTitle}
                            />
                        </div>

                        {isQuestionInquiry && (
                            <div className="pod-v2-contact-popup__question-grid">
                                <div className="pod-v2-contact-popup__select-field">
                                    <span>문항번호</span>
                                    <Dropdown
                                        btnName={
                                            articleOptions.length > 0
                                                ? articleOptions[questionIndex]?.questionNumber
                                                : '문항 정보 없음'
                                        }
                                        className="pod-v2-contact-popup__dropdown"
                                        disabled={articleOptions.length === 0}
                                        onChange={(value: string) =>
                                            setQuestionIndex(Number(value))
                                        }
                                        option={articleOptions.map((item, index) => ({
                                            name: item.questionNumber,
                                            val: String(index),
                                        }))}
                                        selected={String(questionIndex)}
                                    />
                                </div>
                                <label>
                                    <span>문항 key</span>
                                    <span className="pod-v2-contact-popup__key-field">
                                        <input
                                            aria-label="문항 key"
                                            readOnly
                                            value={selectedArticleId}
                                        />
                                        <em>관리자 확인용</em>
                                    </span>
                                </label>
                            </div>
                        )}
                    </section>

                    <label className="pod-v2-contact-popup__row">
                        <strong>제목</strong>
                        <input
                            autoFocus
                            maxLength={100}
                            onChange={(event) => setTitle(event.target.value)}
                            placeholder="제목을 입력해 주세요."
                            value={title}
                        />
                    </label>
                    <label className="pod-v2-contact-popup__row is-content">
                        <strong>내용</strong>
                        <textarea
                            onChange={(event) => setContents(event.target.value)}
                            placeholder={contentsPlaceholder}
                            value={contents}
                        />
                    </label>

                    <footer className="pod-v2-contact-popup__footer">
                        <p>※ 비바샘 회원정보의 이메일로 답변이 발송됩니다.</p>
                        <button type="submit">문의하기</button>
                    </footer>
                </form>
            </section>
        </div>
    );
};
